import React from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import logo from '../../assets/logo.png'

const ALL_NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/lessons', label: 'Lessons', icon: '📋', feature: 'lessons' },
  { path: '/near-miss', label: 'Near Miss / Good Catch', icon: '🚨', feature: 'lessons' },
  { path: '/jha', label: 'Job Hazard Analysis', icon: '🛡️', feature: 'jha' },
  { path: '/safety', label: 'Safety Manuals', icon: '📄', feature: 'safety' },
  { path: '/handbook', label: 'Employee Handbook', icon: '📘', feature: 'safety' },
  { path: '/communications', label: 'Communications', icon: '📨', feature: 'communications' },
  { path: '/reports', label: 'Daily Reports', icon: '📋', feature: 'reports' },
  { path: '/timesheets', label: 'Timesheets', icon: '⏱️', feature: 'timesheets' },
  { path: '/attendance', label: 'Attendance', icon: '🕐' },
  { path: '/certifications', label: 'Certifications', icon: '🎓', feature: 'certifications' },
  { path: '/equipment', label: 'Equipment', icon: '🔧', feature: 'equipment' },
  { path: '/tasks', label: 'Tasks', icon: '✅', feature: 'tasks' },
  { path: '/incidents', label: 'Incidents', icon: '🩹', feature: 'incidents' },
  // Admin-only nav items
  { path: '/users', label: 'Users', icon: '👥', feature: 'users', adminOnly: true },
  { path: '/crew', label: 'Crew Mgmt', icon: '👷', feature: 'users', adminOnly: true },
  { path: '/crewmap', label: 'Crew Map', icon: '🗺️', feature: 'crewmap', adminOnly: true, projectOnly: true },
  { path: '/projects', label: 'Projects', icon: '📁', adminOnly: true },
  { path: '/settings', label: 'Settings', icon: '⚙️', feature: 'settings', adminOnly: true },
]

const PLAN_LABELS: Record<string, string> = {
  core: 'Core', field: 'Field', premium: 'Premium', enterprise: 'Enterprise'
}

export default function AppLayout({ children, title }: { children: React.ReactNode; title?: string }) {
  const { userData, orgData, orgId, projects, selectedProjectId, selectedProject, setSelectedProjectId, logout, hasFeature, isProjectView, isAdmin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const admin = isAdmin()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const roleColors: Record<string, string> = {
    admin: 'bg-red-100 text-red-700',
    supervisor: 'bg-blue-100 text-blue-700',
    superintendant: 'bg-amber-100 text-amber-700',
    foreman: 'bg-purple-100 text-purple-700',
    member: 'bg-green-100 text-green-700',
  }

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProjectId(e.target.value)
    // If currently on a project-only admin page and switching to org view, redirect to dashboard
    if (!e.target.value) {
      if (['/crewmap'].includes(location.pathname)) {
        navigate('/')
      }
    }
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-60 bg-sidebar flex flex-col fixed h-full">
        {/* Logo */}
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={logo} alt="SiteDeck Pro" className="h-8 w-auto" />
            <span className="text-white font-bold text-base">Site Deck Pro</span>
          </div>
          {orgData && (
            <p className="text-xs text-white/60 mt-0.5 truncate">{orgData.name}</p>
          )}
        </div>

        {/* Project selector */}
        <div className="px-5 py-3 border-b border-white/10">
          <label className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1.5 block">
            {admin ? 'Select Project' : 'Your Project'}
          </label>
          <select
            value={selectedProjectId}
            onChange={handleProjectChange}
            disabled={!admin}
            className={`w-full text-sm rounded px-2 py-1.5 bg-white/10 border border-white/10 text-white ${admin ? 'cursor-pointer hover:bg-white/15' : 'opacity-70 cursor-default'}`}
          >
            {admin && (
              <option value="" className="bg-sidebar text-white">All Projects (Org View)</option>
            )}
            {projects.length === 0 && (
              <option value="" className="bg-sidebar text-white/60">No projects yet</option>
            )}
            {projects.map(p => (
              <option key={p.id} value={p.id} className="bg-sidebar text-white">
                {p.name}
              </option>
            ))}
          </select>
          {isProjectView && selectedProject && (
            <p className="text-[10px] text-white/40 mt-1 truncate">
              {selectedProject.code || selectedProject.id.slice(0, 6)}
            </p>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {ALL_NAV_ITEMS.map(item => {
            if (item.adminOnly && !admin) return null
            if (item.projectOnly && !isProjectView) return null
            if (item.feature === 'settings' && !admin) return null
            if (item.feature === 'crewmap' && !hasFeature('crewmap')) return null

            const locked = item.feature && !hasFeature(item.feature)
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-accent text-white border-r-2 border-accent'
                      : locked
                      ? 'text-white/30 cursor-not-allowed'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <span>{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                {locked && <span className="text-xs">🔒</span>}
              </NavLink>
            )
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-white font-semibold text-sm">
              {(userData?.name || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{userData?.name || 'User'}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColors[userData?.role || 'member']}`}>
                {userData?.role || 'member'}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full text-left text-sm text-white/50 hover:text-red-400 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-60 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{title || 'Dashboard'}</h2>
          <div className="flex items-center gap-3">
            {orgData && (
              <span className="text-xs bg-amber-50 text-accent px-3 py-1 rounded-full font-medium">
                {PLAN_LABELS[orgData.plan] || 'Core'} Plan
              </span>
            )}
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium">
              {(userData?.name || '?')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-8 overflow-y-auto bg-gray-100">
          {children}
        </main>
      </div>
    </div>
  )
}
